import { create } from 'zustand';
import { z } from 'zod';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { User } from '../types';
import { stakeholderTypeSchema } from '../schemas/firestoreSchemas';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('onboarding');

const userCacheKey = (uid: string) => `onboarding-cache:${uid}`;

const CachedOnboardingSchema = z.object({
  hasCompletedOnboarding: z.boolean(),
  isInServiceArea: z.boolean().nullable(),
  serviceAreaName: z.string().nullable(),
  stakeholderType: stakeholderTypeSchema.nullable(),
});

type CachedOnboarding = z.infer<typeof CachedOnboardingSchema>;

// Read-only remnant of the forced-onboarding era: the screens that mutated
// this state are Redirect stubs, so the store now only mirrors the user doc
// (via hydrate) for legacy accounts. The AsyncStorage cache is read-only
// back-compat — nothing writes it anymore.
interface OnboardingState extends CachedOnboarding {
  hydrate: (
    user: Pick<
      User,
      'hasCompletedOnboarding' | 'isInServiceArea' | 'serviceAreaName' | 'stakeholderType'
    > | null
  ) => void;
}

const EMPTY: CachedOnboarding = {
  hasCompletedOnboarding: false,
  isInServiceArea: null,
  serviceAreaName: null,
  stakeholderType: null,
};

export async function readOnboardingCache(uid: string): Promise<CachedOnboarding | null> {
  try {
    const raw = await AsyncStorage.getItem(userCacheKey(uid));
    if (!raw) return null;
    // Runtime-validate: AsyncStorage is technically user-writable on a
    // compromised device. Schema-check keeps tampered data from flowing
    // into state as though it were our own.
    const parsed = CachedOnboardingSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch (err) {
    log.error(' failed to read cache:', err);
    return null;
  }
}

export const useOnboardingStore = create<OnboardingState>()((set) => ({
  ...EMPTY,

  hydrate: (user) => {
    if (!user) {
      set(EMPTY);
      return;
    }
    set({
      hasCompletedOnboarding: user.hasCompletedOnboarding ?? false,
      isInServiceArea: user.isInServiceArea ?? null,
      serviceAreaName: user.serviceAreaName ?? null,
      stakeholderType: user.stakeholderType ?? null,
    });
  },
}));
